import * as React from "react";

import type { Activity, ActivityRequest, GetActivity } from "./activity";
import { type Deferred, deferred, NEVER, NO_SUBSCRIPTION } from "./loading";

export type ActivityResult =
  | { status: "pending" }
  | { status: "success"; activity: Activity }
  | { status: "error"; error: Error };

const PENDING: ActivityResult = Object.freeze({ status: "pending" });

/**
 * One `getActivity` call as a Suspense data source: the request starts on
 * construction, `get()` is the snapshot, `whenLoaded()` the promise a
 * suspended component throws, `retry()` starts over after a failure.
 */
export class ActivityLoader {
  /** Unique per loader, so a view can be keyed on it */
  readonly id = crypto.randomUUID();
  #result: ActivityResult = PENDING;
  #request!: { controller: AbortController; settled: Deferred };
  #disposed = false;
  readonly #listeners = new Set<() => void>();

  constructor(
    private readonly getActivity: GetActivity,
    private readonly request: ActivityRequest,
  ) {
    this.#start();
  }

  get(): ActivityResult {
    return this.#result;
  }

  /** Resolves when the request settles; already resolved once it has; never, once disposed. */
  whenLoaded(): Promise<void> {
    return this.#disposed ? NEVER : this.#request.settled.promise;
  }

  /** After a failure, ask again. Notifies, so a subscriber re-renders into the pending state. */
  retry(): void {
    if (this.#disposed || this.#result.status !== "error") {
      return;
    }
    this.#result = PENDING;
    this.#start();
    this.#notify();
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  /** Abort the request; a result that arrives afterwards is dropped. */
  dispose(): void {
    this.#disposed = true;
    this.#request.settled.resolve();
    this.#request.controller.abort();
  }

  #start(): void {
    const request = { controller: new AbortController(), settled: deferred() };
    this.#request = request;
    this.getActivity(this.request, request.controller.signal).then(
      (activity) => this.#publish(request, { status: "success", activity }),
      (error: unknown) =>
        this.#publish(request, {
          status: "error",
          error: error instanceof Error ? error : new Error(String(error)),
        }),
    );
  }

  #publish(
    request: { controller: AbortController; settled: Deferred },
    result: ActivityResult,
  ): void {
    request.settled.resolve();
    if (request.controller.signal.aborted) {
      return;
    }
    this.#result = result;
    this.#notify();
  }

  #notify(): void {
    for (const listener of this.#listeners) {
      listener();
    }
  }
}

/**
 * One loader per `getActivity` identity and bucket count, created inside the effect that
 * disposes it (StrictMode runs cleanup and the effect again, and a disposed
 * loader must not be reused). `null` until the first effect has run.
 */
export function useActivityLoader(
  getActivity: GetActivity,
  bucketCount: number,
): ActivityLoader | null {
  const [loader, setLoader] = React.useState<ActivityLoader | null>(null);

  React.useEffect(() => {
    const next = new ActivityLoader(getActivity, { bucketCount });
    setLoader(next);
    return () => {
      next.dispose();
      setLoader((current) => (current === next ? null : current));
    };
  }, [getActivity, bucketCount]);

  return loader;
}

/** The loader's result as a React snapshot; pending without a loader. */
export function useActivityResult(
  loader: ActivityLoader | null,
): ActivityResult {
  return React.useSyncExternalStore(
    loader?.subscribe ?? NO_SUBSCRIPTION,
    () => loader?.get() ?? PENDING,
  );
}
